import { HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { clearLocalStorageUtil } from "@utils/clear-local-storage.util";
import { filter, tap } from "rxjs/operators";
import { AppStateService } from "../services";
import {
  ApiResponseTrackerService,
  IRequestMethod,
} from "../services/api-response-tracker/api-response-tracker.service";
import { BACKGROUND_REQUEST, silentSessionExpired } from "./silent-request.token";

@Injectable({ providedIn: "root" })
export class HttpRequestInterceptor implements HttpInterceptor {
  constructor(
    private apiResponseTrackerService: ApiResponseTrackerService,
    private appStateService: AppStateService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    // FormData bodies (multipart uploads, e.g. vendor profile picture / the CDN image
    // uploader) must pass through untouched: spreading a FormData instance (`{ ...req.body }`)
    // strips it down to `{}` since FormData exposes no own enumerable properties, and forcing
    // a JSON content-type overrides the multipart boundary the browser sets automatically.
    const isFormData = req.body instanceof FormData;
    const body = isFormData ? req.body : { ...req.body };

    // Session now lives in the httpOnly `ab_session` cookie (no more
    // `x-access-token` header from localStorage) — `withCredentials: true`
    // makes sure it's sent with every request.
    const clonedAuthReq = req.clone({
      setHeaders: isFormData
        ? { Accept: "application/json" }
        : {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            "Content-Type": "application/json; charset=utf-8",
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Accept: "application/json",
          },
      body,
      withCredentials: true,
    });

    return next.handle(clonedAuthReq).pipe(
      filter((event: any) => event instanceof HttpResponse),
      tap((event: any) => {
        this.apiResponseTrackerService.saveResponse({
          url: event.url!,
          data: event.body,
          method: clonedAuthReq.method as IRequestMethod,
        });
        // A user-initiated request got through (e.g. after logging in again in this tab):
        // the session is back, so the pollers may resume.
        if (
          event.body?.status &&
          event.body?.message !== "Unauthorized" &&
          !req.context.get(BACKGROUND_REQUEST)
        ) {
          silentSessionExpired.set(false);
        }
        // Skip the hard redirect while already on /login: the login page's own
        // bootstrap check (and the app-wide APP_INITIALIZER) also calls
        // /api/v1/auth/session, which legitimately comes back Unauthorized for
        // a logged-out visitor — without this guard that turns into an
        // infinite reload loop (reload -> re-check -> still unauthorized ->
        // reload -> ...).
        if (
          event.body?.message === "Unauthorized" &&
          !window.location.pathname.startsWith("/login")
        ) {
          // A background poll doesn't yank the page away: it flags the expiry so pollers
          // stop; the next user action redirects as below.
          if (req.context.get(BACKGROUND_REQUEST)) {
            silentSessionExpired.set(true);
            return;
          }
          clearLocalStorageUtil();
          this.appStateService.userToken$.next("");
          this.appStateService.currentUser$.next(null);
          // Hard reload rather than router.navigate — an httpOnly cookie
          // can't be inspected/cleared from JS, so a full re-bootstrap is
          // the reliable way to resync app state after an expired session.
          window.location.href = "/login";
        }
      })
    );
  }
}
